import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  selectUserCounty,
  selectUserIsAppellant,
  selectUserIsAuthorizedAttorney,
  selectUserMunicipalities
} from "./app";
import { getActiveAppealYear } from "../pages/DefaultPages/common/CountyUtils";
import pako from "pako";
import { RestStatus } from "../commontypes/common";
import dayjs from "dayjs";
import { RootState, AppDispatch } from '.';
import { Appeal, AppealJudgement, AppealStatus, AppealCategory } from "../commontypes/types";
import { CountyDefinitions } from "../pages/DefaultPages/common/CountyDefinitions";

export enum LoadStatus {
  NOT_LOADED = "not loaded",
  IN_PROGRESS = "in progress",
  SUCCESS = "success",
  FAILED = "failed"
};

interface AppealListState {
  allAppeals: Appeal[];
  workInProgressAppeals: Appeal[];
  stipulatedAppeals: Appeal[];
  readyForBoardAppeals: Appeal[];
  judgmentIssuedAppeals: Appeal[];
  withdrawnAppeals: Appeal[];
  AWOPAppeals: Appeal[];
  scheduleSaveCount: number;
  loadStatus: LoadStatus;
  loadError?: string;
  scheduleStatus?: RestStatus;
  scheduleError?: string;
  judgementStatus?: RestStatus;
  judgementError?: string;
}

const initialState: AppealListState = {
  allAppeals: [],
  workInProgressAppeals: [],
  stipulatedAppeals: [],
  readyForBoardAppeals: [],
  judgmentIssuedAppeals: [],
  withdrawnAppeals: [],
  AWOPAppeals: [],
  scheduleSaveCount: 0,
  loadStatus: LoadStatus.NOT_LOADED
};
export enum FetchAppealsCategory {
  AdditionAppeals = 'ten',
  ValueDeductionAppeals = 'twenty',
  AllAppeals = 'both'
}

export type FetchAppealsParams = {
  appealCategory: FetchAppealsCategory;
  appealYear?: number;
  judgmentSince?: Date;
}

export const fetchAppeals = createAsyncThunk<
  Appeal[],
  FetchAppealsParams,
  { state: RootState }
>(
  'appealList/fetchAppeals',
  async ({ appealCategory: appealType, appealYear, judgmentSince }, { getState }) => {
    const state = getState() as RootState;
    const userIsAppellant = selectUserIsAppellant(state);
    const userIsAuthorizedAttorney = selectUserIsAuthorizedAttorney(state);
    const userCounty = selectUserCounty(state);
    const userMunicipalities = selectUserMunicipalities(state) || [];
    const params: { type: FetchAppealsCategory, appealYear?: number, townId?: string, judgmentSince?: string } = { type: appealType };
    const config = {
      params
    };
    let countyId;
    if (!userIsAppellant && userMunicipalities.length > 0) {
      params.appealYear = appealYear || getActiveAppealYear(userCounty!, appealType === FetchAppealsCategory.AdditionAppeals ? AppealCategory.ADD_OMIT : AppealCategory.VALUE_CLASS);
      params.townId = userMunicipalities.map(m => m.key).join(',');
      countyId = userCounty?.key;
      if (judgmentSince) {
        params.judgmentSince = dayjs(judgmentSince).toJSON();
      }
    } else {
      countyId = '00';
      params.appealYear = appealYear;
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${countyId}/appeals`;
    const response = await axios
      .get(
        path,
        config
      );
    let appeals = JSON.parse(Buffer.from(pako.inflate(response.data.data)).toString()) as Appeal[];
    //console.log("Appeals:", appeals);
    // let countUser = 0;
    // let totalFee: Record<string, number> = {};
    // let cardCount: Record<string, number> = {};
    // let cardList: string[] = [];
    // let totalFeeAll = 0;
    // appeals.sort((a, b) => a._APPEAL_NUMBER.localeCompare(b._APPEAL_NUMBER));
    // for (let i = 0; i < appeals.length; i++) {
    //   const a = appeals[i];
    //   let skipFee = false;
    //   if (i > 0 && appeals[i - 1].APPEAL.property.blq === a.APPEAL.property.blq && appeals[i - 1].APPEAL.municipality.key === a.APPEAL.municipality.key && a.APPEAL.creditCardPayment.authCode && appeals[i - 1].APPEAL.creditCardPayment.authCode && a.APPEAL.creditCardPayment.authCode === appeals[i - 1].APPEAL.creditCardPayment.authCode) {
    //     console.log(`Appeal ${a.APPEAL.appealNumber} has same auth code and property as previous appeal, skipping fee to avoid double counting, fee=${a.APPEAL.fees.total}, cardNumber=${a.APPEAL.creditCardPayment.transactionResponse?.accountNumber}`);
    //     skipFee = true;
    //   }
    //   if (a.APPEAL.entryPaymentMethod === "Online Card" && a.APPEAL.creditCardPayment.authCode === undefined) {
    //     console.error(`Appeal ${a.APPEAL.appealNumber} was paid online but is missing auth code, fee=${a.APPEAL.fees.total}, cardNumber=${a.APPEAL.creditCardPayment.transactionResponse?.accountNumber}`);
    //   } else {
    //     if (!skipFee) {
    //       totalFeeAll += (a.APPEAL.fees.total || 0);
    //     }
    //   }
    //   if (a._UUID == "A5044") {

    //     let cardNumber = a.APPEAL.creditCardPayment.transactionResponse?.accountNumber;
    //     if (!cardNumber) {
    //       cardCount["unknownCard"]++;
    //       totalFee["unknownCard"] += (a.APPEAL.fees.total || 0);
    //     } else {
    //       if (!cardList.includes(cardNumber)) {
    //         cardList.push(cardNumber);
    //       }
    //       if (cardNumber in cardCount) {
    //         cardCount[cardNumber as keyof typeof cardCount]++;
    //       } else {
    //         cardCount[cardNumber as keyof typeof cardCount] = 1;
    //       }
    //       if (!skipFee) {
    //         if (cardNumber in totalFee) {
    //           totalFee[cardNumber as keyof typeof totalFee] += (a.APPEAL.fees.total || 0);
    //         } else {
    //           totalFee[cardNumber as keyof typeof totalFee] = (a.APPEAL.fees.total || 0);
    //         }
    //       }
    //     }
    //     countUser++;
    //   }
    // }
    // console.log(`total fees for all appeals: ${totalFeeAll}`);
    // console.log(`number of appeals with UUID A5044: ${countUser}`);
    // console.log(`total fees for appeals with UUID A5044: ${JSON.stringify(totalFee)}`);
    // console.log(`unique card numbers for appeals with UUID A5044: ${cardList}`);
    // console.log(`card usage count: ${JSON.stringify(cardCount)}`);
    if (userIsAuthorizedAttorney) {
      appeals = appeals.filter(obj => obj.APPEAL.authorizedAttorneyContact?.firstName);
    } else if (appealType !== FetchAppealsCategory.AllAppeals && (userIsAppellant || userMunicipalities.length === 0)) {
      appeals = appeals.filter(obj => appealType === FetchAppealsCategory.AdditionAppeals ? obj.APPEAL.appealNumber.startsWith("10@")
        : !obj.APPEAL.appealNumber.startsWith("10@")) //To handle old appeals that were saved without the 20@
    }
    if (userIsAppellant) {
      appeals = appeals.filter(a => CountyDefinitions.some(c => c.key === a.APPEAL.county.key && c.active));
    }
    return appeals;
  }
);

export type ScheduleAppealsParams = {
  hearingDate: string;
  hearingLocation: string;
  sendNotifications: boolean;
  finalized: boolean;
  appeals: { appealNumber: string; evidenceFreezeDate?: string }[];
}


export const scheduleAppeals = createAsyncThunk<
  Appeal[],
  ScheduleAppealsParams,
  { state: RootState; dispatch: AppDispatch }
>
  (
    'appealList/scheduleAppeals',
    async ({ hearingDate, hearingLocation, sendNotifications, finalized, appeals }, { getState }) => {
      const state = getState();
      const userCounty = selectUserCounty(state);
      if (!userCounty) {
        throw new Error("User county not found");
      }
      const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty.key}/hearingschedule`;
      const data = { hearingDate, hearingLocation, sendNotifications, finalized, appeals };
      const config = {};
      const response = await axios.post(path, data, config);
      return response.data;
    }
  );

export const unscheduleAppeals = createAsyncThunk<
  Appeal[],
  { appeals: string[], unfreeze: boolean },
  { state: RootState; dispatch: AppDispatch }
>(
  'appealList/unscheduleAppeals',
  async ({ appeals, unfreeze }, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    if (!userCounty) {
      throw new Error("User county is not defined");
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty?.key}/hearingschedule`;
    const data = { appeals, removeEvidenceFreeze: unfreeze };
    const config = { data };
    const response = await axios.delete(path, config);
    return response.data;
  }
);

export const holdAppeals = createAsyncThunk<
  Appeal[],
  { appeals: string[] },
  { state: RootState; dispatch: AppDispatch }
>(
  'appealList/holdAppeals',
  async ({ appeals }, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    if (!userCounty) {
      throw new Error("User county is not defined");
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty?.key}/hearingschedule/hold`;
    const data = { appeals };
    const config = {};
    const response = await axios.post(path, data, config);
    return response.data;
  }
);

export const unholdAppeals = createAsyncThunk<
  Appeal[],
  { appeals: string[] },
  { state: RootState; dispatch: AppDispatch }
>(
  'appealList/unholdAppeals',
  async ({ appeals }, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    if (!userCounty) {
      throw new Error("User county is not defined");
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty.key}/hearingschedule/hold`;
    const data = { appeals };
    const config = { data };
    const response = await axios.delete(path, config);
    return response.data;
  }
);

export const judgeMultipleAppeals = createAsyncThunk<
  Appeal[],
  { appeals: AppealJudgement[], sendNotifications: boolean },
  { state: RootState; dispatch: AppDispatch }
>(
  'appealList/judgeMultipleAppeals',
  async ({ appeals, sendNotifications }, { getState }) => {
    const state = getState();
    const userCounty = selectUserCounty(state);
    if (!userCounty) {
      throw new Error("User county is not defined");
    }
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${userCounty.key}/judgements`;
    const data = { appealJudgements: appeals, sendNotifications, judgementStatus: AppealStatus.JudgmentIssued };
    const config = {};
    const response = await axios.post(path, data, config);
    return response.data;
  }
);
export const appealListSlice = createSlice({
  name: 'appealList',
  initialState: initialState,
  reducers: {
    clearAppeals: (state) => {
      state.allAppeals = [];
      state.workInProgressAppeals = [];
      state.stipulatedAppeals = [];
      state.readyForBoardAppeals = [];
      state.judgmentIssuedAppeals = [];
      state.withdrawnAppeals = [];
      state.loadStatus = LoadStatus.NOT_LOADED;
      state.loadError = '';
      state.scheduleStatus = RestStatus.NOT_STARTED;
      state.scheduleError = '';
      state.scheduleSaveCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppeals.pending, (state) => {
        state.loadStatus = LoadStatus.IN_PROGRESS;
        state.loadError = '';
      })
      .addCase(fetchAppeals.rejected, (state, action) => {
        state.loadStatus = LoadStatus.FAILED;
        state.loadError = action.error.message;
      })
      .addCase(fetchAppeals.fulfilled, (state, action) => {
        const appeals = action.payload;
        state.allAppeals = appeals;
        state.workInProgressAppeals = appeals.filter(obj => (obj.APPEAL.status === AppealStatus.WorkInProgress && !obj.APPEAL.valuation.awop));
        state.stipulatedAppeals = appeals.filter(obj => (obj.APPEAL.status === AppealStatus.Stipulated));
        state.readyForBoardAppeals = appeals.filter(obj => (obj.APPEAL.status === AppealStatus.ReadyForBoard && !obj.APPEAL.valuation.awop));
        state.judgmentIssuedAppeals = appeals.filter(obj => (obj.APPEAL.status && (obj.APPEAL.status.toUpperCase() === AppealStatus.JudgmentIssued.toUpperCase())));
        state.withdrawnAppeals = appeals.filter(obj => (obj.APPEAL.status === AppealStatus.Withdrawn));
        state.AWOPAppeals = appeals.filter(obj => obj.APPEAL.valuation.awop && (obj.APPEAL.status === AppealStatus.WorkInProgress || obj.APPEAL.status === AppealStatus.ReadyForBoard));
        state.loadStatus = LoadStatus.SUCCESS;
      })
      .addCase(scheduleAppeals.pending, (state) => {
        state.scheduleStatus = RestStatus.SAVING;
        state.scheduleError = '';
      })
      .addCase(scheduleAppeals.rejected, (state, action) => {
        state.scheduleStatus = RestStatus.SAVE_FAILED;
        state.scheduleError = action.error.message;
      })
      .addCase(scheduleAppeals.fulfilled, (state) => {
        state.scheduleStatus = RestStatus.SAVED;
        state.scheduleSaveCount = state.scheduleSaveCount + 1;
      })
      .addCase(unscheduleAppeals.pending, (state) => {
        state.scheduleStatus = RestStatus.SAVING;
        state.scheduleError = '';
      })
      .addCase(unscheduleAppeals.rejected, (state, action) => {
        state.scheduleStatus = RestStatus.SAVE_FAILED;
        state.scheduleError = action.error.message;
      })
      .addCase(unscheduleAppeals.fulfilled, (state) => {
        state.scheduleStatus = RestStatus.SAVED;
        state.scheduleSaveCount = state.scheduleSaveCount + 1;
      })
      .addCase(holdAppeals.pending, (state) => {
        state.scheduleStatus = RestStatus.SAVING;
        state.scheduleError = '';
      })
      .addCase(holdAppeals.rejected, (state, action) => {
        state.scheduleStatus = RestStatus.SAVE_FAILED;
        state.scheduleError = action.error.message;
      })
      .addCase(holdAppeals.fulfilled, (state) => {
        state.scheduleStatus = RestStatus.SAVED;
        state.scheduleSaveCount = state.scheduleSaveCount + 1;
      })
      .addCase(unholdAppeals.pending, (state) => {
        state.scheduleStatus = RestStatus.SAVING;
        state.scheduleError = '';
      })
      .addCase(unholdAppeals.rejected, (state, action) => {
        state.scheduleStatus = RestStatus.SAVE_FAILED;
        state.scheduleError = action.error.message;
      })
      .addCase(unholdAppeals.fulfilled, (state) => {
        state.scheduleStatus = RestStatus.SAVED;
        state.scheduleSaveCount = state.scheduleSaveCount + 1;
      })
      .addCase(judgeMultipleAppeals.pending, (state) => {
        state.judgementStatus = RestStatus.SAVING;
        state.judgementError = '';
      })
      .addCase(judgeMultipleAppeals.rejected, (state, action) => {
        state.judgementStatus = RestStatus.SAVE_FAILED;
        state.judgementError = action.error.message;
      })
      .addCase(judgeMultipleAppeals.fulfilled, (state) => {
        state.judgementStatus = RestStatus.SAVED;
      })
  },
});

export const { clearAppeals } = appealListSlice.actions;

export const selectAllAppeals = (state: RootState) => state.appealList.allAppeals;
export const selectWorkInProgressAppeals = (state: RootState) => state.appealList.workInProgressAppeals;
export const selectStipulatedAppeals = (state: RootState) => state.appealList.stipulatedAppeals;
export const selectReadyForBoardAppeals = (state: RootState) => state.appealList.readyForBoardAppeals;
export const selectJudgmentIssuedAppeals = (state: RootState) => state.appealList.judgmentIssuedAppeals;
export const selectWithdrawnAppeals = (state: RootState) => state.appealList.withdrawnAppeals;
export const selectAWOPAppeals = (state: RootState) => state.appealList.AWOPAppeals;
export const selectAppealListLoadStatus = (state: RootState) => state.appealList.loadStatus;
export const selectAppealListLoadError = (state: RootState) => state.appealList.loadError;
export const selectJudgementSaveStatus = (state: RootState) => state.appealList.judgementStatus;
export const selectJudgementSaveError = (state: RootState) => state.appealList.judgementError;
export const selectScheduleAppealsSaveStatus = (state: RootState) => state.appealList.scheduleStatus;
export const selectScheduleAppealsSaveError = (state: RootState) => state.appealList.scheduleError;
export const selectScheduleAppealsSaveCount = (state: RootState) => state.appealList.scheduleSaveCount;

export default appealListSlice.reducer;